import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Content from '../../components/Content/Content';
import ProtectedPageSidebar from '../../components/Sidebar/ProtectedPageSidebar';
import { useUserContext } from '../../context/UserContext';
import MyLibraryButton from '../../components/MyLibraryButton/MyLibraryButton';
import styles from './CompanySettings.module.scss';
import SubtitleStyle from './components/SubtitleStyle/SubtitleStyle';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import ScriptInput from './components/ScriptInput/ScriptInput';
import Users from './components/Users/Users';
import { useHistory } from 'react-router-dom';
import { withUserRoleCheck } from '../../hocs/withUserRoleCheck';
import LogoSettings from './components/Logos/LogoSettings';
import TextPanelsSettings from './components/TextPanels/TextPanelsSettings';
import { useLocation } from 'react-router-dom';
import FontsList from '../../features/CompanySettings/FontsManagement/components/FontsList/FontsList';
import VormatsAPI from '../../../common/apis/VormatsAPI';
import CompanySelection from '../../components/CompanySelection/CompanySelection';
import UsagePage from '../../features/CompanySettings/Usage/UsagePage';
import queryString from 'query-string';
import Tags from './components/Tags/Tags';
import FeaturesList from '../../features/CompanySettings/FeaturesManagement/FeaturesList/FeaturesList';

const OPTIONS = {
  SCRIPT_INPUT: {
    name: 'company.settings.script_input.title',
    children: <ScriptInput />,
  },
  SUBTITLE_STYLE: {
    name: 'company.settings.subtitle_style.title',
    children: <SubtitleStyle />,
  },
  USERS: {
    name: 'company.settings.users.title',
    children: <Users />,
  },
  LOGOS: {
    name: 'company.settings.logos.title',
    children: <LogoSettings />,
  },
  TEXT_PANELS: {
    name: 'company.settings.text_panels.title',
    children: <TextPanelsSettings />,
  },
  TAGS: {
    name: 'company.settings.tags.title',
    children: <Tags />,
  },
  FONTS: {
    name: 'company.settings.fonts.title',
    children: <FontsList />,
  },
  FEATURES: {
    name: 'company.settings.features.title',
    children: <FeaturesList />,
  },
  USAGE: {
    name: 'company.settings.usage.title',
    children: <UsagePage />,
  },
};

const CompanySettings = () => {
  const {
    user,
    userCompanyId,
    appConfig,
    isStoryboardGeneratorEnabled,
    isUserManagementEnabled,
    isLogoManagementEnabled,
    isTextPanelManagementEnabled,
    isVideoTagsEnabled,
    isMinutesModelEnabled,
    isSuperAdmin,
    isTypebotEnabled
  } = useUserContext();

  const { t } = useTranslation();

  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView, setIsMobileView] = useState(mediaMatch.matches);
  const history = useHistory();
  const location = useLocation();
  const [companiesList, setCompaniesList] = useState([]);
  const currentCompany = useMemo(() => companiesList.find(item => item.id === Number(queryString.parse(location.search)?.companyId)), [
    companiesList,
    location,
  ]);

  const companyId = useMemo(() => isSuperAdmin ? currentCompany?.id ?? null : userCompanyId, [currentCompany, userCompanyId, isSuperAdmin])

  const currentState = useMemo(() => location.state?.option, [location]);

  const subHeaderText = useMemo(() => {
    if (isSuperAdmin) {
      return currentCompany?.name ?? '';
    } else {
      return user?.userFullName ? `${user?.userFullName} ${appConfig?.name ? ' / ' + appConfig.name : ''}` : '';
    }
  }, [isSuperAdmin, user, appConfig, currentCompany]);

  const handleChangeOption = useCallback(
    option => {
      history.push({
        pathname: `/eg/company-settings`,
        state: { option },
        search: location.search,
      });
    },
    [location]
  );

  const handleSelectCompany = useCallback(
    company => {
      history.push({
        pathname: `/eg/company-settings`,
        state: { option: location.state.option },
        search: `companyId=${company.id}`,
      });
    },
    [location]
  );

  useEffect(() => {
    if (isMobileView) {
      history.push('/eg/home');
      return;
    }
  }, [isMobileView]);

  const additionalCheck = useCallback(
    optionName => {
      switch (optionName) {
        case OPTIONS.SCRIPT_INPUT.name:
          return isStoryboardGeneratorEnabled || isTypebotEnabled;
        case OPTIONS.SUBTITLE_STYLE.name:
          return true;
        case OPTIONS.USERS.name:
          return isUserManagementEnabled;
        case OPTIONS.LOGOS.name:
          return isLogoManagementEnabled;
        case OPTIONS.FONTS.name:
          return isSuperAdmin;
        case OPTIONS.TEXT_PANELS.name:
          return isTextPanelManagementEnabled;
        case OPTIONS.TAGS.name:
          return isVideoTagsEnabled;
        case OPTIONS.FEATURES.name:
          return isSuperAdmin;
        case OPTIONS.USAGE.name:
          return isMinutesModelEnabled || isSuperAdmin;
        default:
          return false;
      }
    },
    [
      isStoryboardGeneratorEnabled,
      isUserManagementEnabled,
      isLogoManagementEnabled,
      isSuperAdmin,
      isTextPanelManagementEnabled,
      isMinutesModelEnabled,
    ]
  );

  useEffect(() => {
    const listCompaniesForSuperAdmin = async () => {
      const companies = await VormatsAPI.getCompaniesList();
      setCompaniesList(companies);
      if (!queryString.parse(location.search)?.companyId) {
        const userCompany = companies.find(company => company.id === userCompanyId);
        history.replace({
          pathname: location.pathname,
          state: location?.state ?? { option: Object.keys(OPTIONS)[(isStoryboardGeneratorEnabled || isTypebotEnabled) ? 0 : 1] },
          search: `companyId=${userCompany.id}`,
        });
      }
    };
    if (isSuperAdmin) {
      listCompaniesForSuperAdmin();
    } else {
      !location?.state?.option &&
        history.replace({
          pathname: location.pathname,
          state: { option: Object.keys(OPTIONS)[(isStoryboardGeneratorEnabled || isTypebotEnabled) ? 0 : 1] },
        });
    }
  }, [isSuperAdmin, userCompanyId]);

  return (
    <>
      <ProtectedPageSidebar />
      <Content className={styles.content}>
        <div className={styles.mainWrapper}>
          <div className={styles.mainText}>
            <div className={styles.headerContainer}>
              <h1>{t('company.settings.title')}</h1>
              {isSuperAdmin && (
                <CompanySelection onSelect={handleSelectCompany} currentCompany={currentCompany} companies={companiesList} />
              )}
            </div>
            <div>
              <h2 className={styles.userInfo}>{subHeaderText}</h2>
            </div>
          </div>
          <div className={styles.settingsTabs}>
            {Object.entries(OPTIONS).map(([optionKey, optionValue]) => {
              return (
                additionalCheck(optionValue?.name) && (
                  <MyLibraryButton
                    key={optionKey}
                    className={cx(styles.tabButton, currentState === optionKey && styles.activeTab)}
                    text={t(optionValue?.name)}
                    onClick={() => handleChangeOption(optionKey)}
                  />
                )
              )
            })}
          </div>
          {companyId &&
            OPTIONS[currentState]?.children &&
            React.cloneElement(OPTIONS[currentState]?.children, { companyId: companyId, companyName: currentCompany?.name })}
        </div>
      </Content>
    </>
  );
};

export default withUserRoleCheck(CompanySettings);
